/* Agentes de IA (Painel do Cliente).
 *
 * A personalidade da Sofia agora é do cliente: ele cria agentes (prompt,
 * modelo) e reutiliza entre campanhas. Ligada ao real `/api/agents`
 * (permissão gerir_campanhas, escopada por tenant). Variante clara do Nebula. */

import React, { useEffect, useMemo, useState } from 'react';

import { api } from '../api/cliente';
import { Aviso, Button, card, Cabecalho, Icon, IconButton, Select, Tag } from '../admin/ui';
import { useRecurso } from '../estado/recurso';

const MODELOS = ['gpt-5.6-luna', 'gpt-5.2', 'gpt-4.1', 'gpt-4.1-mini', 'gpt-4o'];

export default function Agentes() {
  const recurso = useRecurso((sinal) => api.agentes(sinal), []);
  const agentes = useMemo(() => recurso.dados?.itens || [], [recurso.dados]);
  const [selId, setSelId] = useState(null);
  const [criando, setCriando] = useState(false);
  const selecionado = agentes.find((a) => a.agente_id === selId) || null;

  // Enquanto o pipeline resolve a personalidade por empresa, só vale o agente
  // único: com dois ou mais, o bot não troca de voz por campanha ainda.
  const porEmpresa = useMemo(() => {
    const contagem = {};
    for (const a of agentes) contagem[a.empresa_id] = (contagem[a.empresa_id] || 0) + 1;
    return contagem;
  }, [agentes]);

  useEffect(() => {
    if (!selId && agentes.length) setSelId(agentes[0].agente_id);
  }, [agentes, selId]);

  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <Cabecalho
        titulo="Agentes de IA"
        descricao="A personalidade da Sofia. Crie um agente e reutilize em quantas campanhas quiser."
        acoes={<Button variant="primary" icon="plus" onClick={() => setCriando(true)}>Novo agente</Button>}
      />

      {criando && <NovoAgente aoFechar={() => setCriando(false)} aoCriado={() => recurso.recarregar()} />}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,300px) minmax(0,1fr)', gap: 20, flex: 1, minHeight: 0, padding: '0 24px 24px' }}>
        <section style={{ ...card }}>
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {recurso.erro ? (
              <Aviso titulo="Os agentes não carregaram" texto={recurso.erro.mensagem} acao={recurso.erro.temporario ? 'Tentar de novo' : null} aoAcao={recurso.recarregar} />
            ) : !recurso.carregando && agentes.length === 0 ? (
              <Aviso titulo="Nenhum agente ainda" texto="Crie o primeiro para dar voz à Sofia." acao="Novo agente" aoAcao={() => setCriando(true)} />
            ) : (
              agentes.map((a) => {
                const ativo = a.agente_id === selId;
                return (
                  <button key={a.agente_id} type="button" onClick={() => setSelId(a.agente_id)} style={{ display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer', padding: '13px 20px', border: 0, borderBottom: '1px solid var(--border-subtle)', background: ativo ? 'var(--surface-active)' : 'transparent', boxShadow: ativo ? 'inset 3px 0 0 var(--accent)' : 'none' }}>
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ font: 'var(--type-body-strong)', color: 'var(--text-primary)' }}>{a.nome}</span>
                      {!a.ativo && <Tag tone="neutral">Inativo</Tag>}
                    </span>
                    <span style={{ display: 'block', font: 'var(--type-caption)', color: 'var(--text-muted)' }}>{a.model || '—'} · {a.campanhas} campanha(s)</span>
                  </button>
                );
              })
            )}
          </div>
        </section>

        {selecionado ? (
          <Editor
            key={selecionado.agente_id}
            agente={selecionado}
            multiplos={(porEmpresa[selecionado.empresa_id] || 0) > 1}
            aoSalvar={() => recurso.recarregar()}
          />
        ) : (
          <section style={{ ...card, alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)' }}>Selecione um agente.</span>
          </section>
        )}
      </div>
    </div>
  );
}

const rot = { font: 'var(--type-label)', color: 'var(--text-secondary)' };
const inp = { height: 40, padding: '0 12px', width: '100%', background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-control)', color: 'var(--text-primary)', font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', outline: 'none' };

function Editor({ agente, multiplos, aoSalvar }) {
  const [nome, setNome] = useState(agente.nome || '');
  const [instrucoes, setInstrucoes] = useState(agente.instrucoes || '');
  const [model, setModel] = useState(agente.model || MODELOS[0]);
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);

  const modelos = useMemo(() => {
    const lista = MODELOS.includes(model) ? MODELOS : [model, ...MODELOS];
    return lista.map((m) => ({ value: m, label: `Modelo · ${m}` }));
  }, [model]);

  const mudou = nome !== agente.nome || instrucoes !== (agente.instrucoes || '') || model !== (agente.model || MODELOS[0]);

  async function salvar() {
    setSalvando(true);
    try {
      await api.salvarAgente(agente.agente_id, { nome, instrucoes, model });
      setSalvo(true);
      aoSalvar?.();
      setTimeout(() => setSalvo(false), 2200);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <section style={{ ...card }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 18, padding: '20px 22px' }}>
        {multiplos && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '11px 13px', background: 'var(--warning-soft)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-control)' }}>
            <span style={{ flex: 'none', display: 'flex', color: 'var(--warning-text)', paddingTop: 1 }}><Icon name="triangle-alert" size={15} /></span>
            <span style={{ font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', color: 'var(--text-secondary)' }}>
              Esta empresa tem mais de um agente. Por enquanto o atendimento usa
              uma única personalidade por empresa — a troca de agente por campanha
              passa a valer quando a resolução por campanha entrar no ar. Até lá,
              editar aqui pode não mudar o que a Sofia responde.
            </span>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={rot}>Nome do agente</span>
            <input style={inp} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Sofia acolhedora" />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={rot}>Modelo</span>
            <Select value={model} onChange={(e) => setModel(e.target.value)} fullWidth options={modelos} />
          </label>
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minHeight: 0 }}>
          <span style={rot}>Prompt / personalidade</span>
          <textarea value={instrucoes} onChange={(e) => setInstrucoes(e.target.value)} placeholder="Quem a Sofia é nesta empresa, o tom, o que ela oferece, as regras…" style={{ ...inp, height: 'auto', minHeight: 280, flex: 1, padding: 12, lineHeight: 'var(--lh-relaxed)', resize: 'vertical' }} />
          <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>Comportamento e tom. Formato da resposta e mecânica da jornada são da plataforma — não escreva aqui.</span>
        </label>
      </div>
      <footer style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '0 0 auto', padding: '14px 22px', borderTop: '1px solid var(--border-subtle)', background: 'var(--surface-sunken)' }}>
        <Button variant="primary" onClick={salvar} disabled={salvando || !mudou} style={salvando || !mudou ? { opacity: 0.6, cursor: 'default' } : undefined}>{salvando ? 'Salvando…' : 'Salvar agente'}</Button>
        {salvo && <span style={{ font: 'var(--type-caption)', color: 'var(--success-text)' }}>Salvo</span>}
      </footer>
    </section>
  );
}

function NovoAgente({ aoFechar, aoCriado }) {
  const tenants = useRecurso((sinal) => api.tenants(sinal), []);
  const empresas = useMemo(() => tenants.dados || [], [tenants.dados]);
  const [nome, setNome] = useState('');
  const [sender, setSender] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);

  useEffect(() => { if (!sender && empresas.length) setSender(empresas[0].sender); }, [empresas, sender]);
  const valido = nome.trim() && sender;

  async function enviar() {
    setEnviando(true);
    setErro(null);
    try {
      await api.criarAgente({ sender, nome: nome.trim(), instrucoes: '', model: MODELOS[0] });
      aoCriado?.();
      aoFechar();
    } catch (e) {
      setErro(e.mensagem || 'Não foi possível criar.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(4,5,20,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 6 }}>
      <div style={{ ...card, width: 440, maxWidth: '100%', boxShadow: 'var(--shadow-md)' }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
          <h3 style={{ margin: 0, font: 'var(--type-h3)' }}>Novo agente</h3>
          <IconButton icon="x" label="Fechar" onClick={aoFechar} />
        </header>
        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={rot}>Nome do agente</span>
            <input style={inp} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Sofia acolhedora" />
          </label>
          {empresas.length > 1 && (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={rot}>Empresa</span>
              <Select value={sender} onChange={(e) => setSender(e.target.value)} fullWidth options={empresas.map((t) => ({ value: t.sender, label: t.nome || t.sender }))} />
            </label>
          )}
          <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>O prompt você escreve depois, no editor.</span>
          {erro && <span style={{ font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', color: 'var(--danger-text)' }}>{erro}</span>}
        </div>
        <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 20px', borderTop: '1px solid var(--border-subtle)', background: 'var(--surface-sunken)' }}>
          <Button variant="ghost" onClick={aoFechar}>Cancelar</Button>
          <Button variant="primary" onClick={enviar} disabled={!valido || enviando} style={!valido || enviando ? { opacity: 0.6, cursor: 'default' } : undefined}>{enviando ? 'Criando…' : 'Criar'}</Button>
        </footer>
      </div>
    </div>
  );
}
